import { IconProps } from '@euler/app/components/icons/types';
import { Typography } from '@euler/app/components/typography';
import { DefectiveIcon } from '@euler/app/tv/components/icons/defective-icons/DefectiveIcon';
import { NormalIcon } from '@euler/app/tv/components/icons/defective-icons/NormalIcon';
import { UrgentIcon } from '@euler/app/tv/components/icons/defective-icons/UrgentIcon';
import { WarningIcon } from '@euler/app/tv/components/icons/defective-icons/WarningIcon';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Center } from '@euler/components/Center';
import { CircularProgress } from '@euler/components/CircularProgress';
import { HStack } from '@euler/components/layout/Stack';
import { getDefectiveLevelText } from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  DefectiveLevel,
  DefectiveLevelList,
  defectiveLevelTokenMapToValueMap,
  DefectiveSummary,
} from '@euler/model';
import { comparer } from '@euler/utils';
import { sorted } from '@euler/utils/array';
import { FC, memo, ReactNode, useCallback, useEffect, useMemo } from 'react';
import {
  StyleProp,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import { useSharedValue, withTiming } from 'react-native-reanimated';

export type DefectiveChartItemPropsBase = {
  labelStyle?: StyleProp<TextStyle>;
  counterStyle?: StyleProp<TextStyle>;
  labelContainerStyle?: StyleProp<ViewStyle>;
  noLabel?: boolean;
  size?: number;
  strokeWidth?: number;
  strokeColor: string;
  trackColor?: string;
};

type CellProps = {
  value: number;
  count: number;
  defectiveLevel: DefectiveLevel;
  style?: StyleProp<ViewStyle>;
  disabled?: boolean;
  labelIcon?: ReactNode;
  onPress?: (defectiveLevel: DefectiveLevel) => void;
} & DefectiveChartItemPropsBase;

const DefectiveChartCell = memo(({ style, onPress, ...props }: CellProps) => {
  const handlePress = useCallback(() => {
    onPress?.(props.defectiveLevel);
  }, [onPress, props.defectiveLevel]);

  if (onPress != null) {
    return (
      <TouchableOpacity
        css={`
          justify-content: center;
          align-items: center;
        `}
        style={style}
        onPress={handlePress}
      >
        <CellContent {...props} />
      </TouchableOpacity>
    );
  }

  return (
    <Center style={style}>
      <CellContent {...props} />
    </Center>
  );
});

const CellContent = memo(
  ({
    size,
    defectiveLevel,
    noLabel,
    count,
    value,
    strokeWidth,
    labelStyle,
    labelContainerStyle,
    counterStyle,
    disabled,
    labelIcon,
    strokeColor,
    trackColor,
  }: Omit<CellProps, 'style'>) => {
    const { rpx } = useResponsiveContext();
    const {
      task: {
        inspection: {
          defectiveChart: { labelColor, disabledLabelColor },
        },
      },
    } = usePresentationTheme();

    const progress = useSharedValue(0);
    useEffect(() => {
      progress.value = withTiming(value, { duration: 1000 });
    }, [progress, value]);

    return (
      <>
        <CircularProgress
          progress={progress}
          stroke={strokeColor}
          track={trackColor}
          strokeWidth={strokeWidth ?? rpx(5)}
          size={size ?? rpx(60)}
        >
          <Center style={StyleSheet.absoluteFill}>
            <Typography.Label
              bold
              size={((size ?? rpx(60)) * 1.2) / 4}
              lineHeight={size}
              style={counterStyle}
            >
              {count}
            </Typography.Label>
          </Center>
        </CircularProgress>
        {!noLabel && (
          <HStack justifyContent="center" style={labelContainerStyle}>
            {labelIcon}
            <Typography.Label
              regular
              size={rpx(10)}
              color={disabled ? disabledLabelColor : labelColor}
              css={`
                margin-top: ${rpx(4)}px;
                text-align: center;
              `}
              style={labelStyle}
            >
              {defectiveLevel === DefectiveLevel.Fine
                ? '正常状态'
                : getDefectiveLevelText(defectiveLevel)}
            </Typography.Label>
          </HStack>
        )}
      </>
    );
  },
);

const DefectiveIconMap: { [p in DefectiveLevel]: FC<IconProps> } = {
  [DefectiveLevel.Urgent]: UrgentIcon,
  [DefectiveLevel.Warning]: WarningIcon,
  [DefectiveLevel.Defective]: DefectiveIcon,
  [DefectiveLevel.Fine]: NormalIcon,
};

export type DefectiveChartPropsBase = {
  style?: StyleProp<ViewStyle>;
  iconStyle?: StyleProp<ViewStyle>;
  showZero?: boolean;
  alwaysEnabled?: boolean;
  showIcons?: boolean;
  iconSize?: number;
  itemStyle?: StyleProp<ViewStyle>;
  onCellPress?: (defectiveLevel: DefectiveLevel) => void;
} & Omit<DefectiveChartItemPropsBase, 'strokeColor' | 'trackColor'>;

export const DefectiveChart = memo(
  ({
    summary,
    total,
    size,
    style,
    iconStyle,
    showZero,
    alwaysEnabled,
    showIcons,
    iconSize,
    itemStyle,
    counterStyle,
    onCellPress,
    ...props
  }: {
    summary: DefectiveSummary;
    total: number;
  } & DefectiveChartPropsBase) => {
    const {
      task: {
        inspection: {
          defectiveChart: {
            iconColor,
            disabledIconColor,
            defectiveColor,
            counterColor,
            disabledCounterColor,
            circleBgColor,
          },
        },
      },
    } = usePresentationTheme();
    const colorMap = useMemo(
      () => defectiveLevelTokenMapToValueMap(defectiveColor),
      [defectiveColor],
    );
    return (
      <HStack justifyContent="flex-end" style={style}>
        {sorted(DefectiveLevelList, comparer).map(defectiveLevel =>
          showZero || (summary[defectiveLevel] > 0 && total > 0) ? (
            <DefectiveChartCell
              size={size}
              defectiveLevel={defectiveLevel}
              count={summary[defectiveLevel]}
              key={defectiveLevel}
              disabled={total === 0 && !alwaysEnabled}
              value={total === 0 ? 0 : summary[defectiveLevel] / total}
              style={itemStyle}
              onPress={onCellPress}
              counterStyle={[
                {
                  color:
                    total === 0 && !alwaysEnabled
                      ? disabledCounterColor
                      : summary[defectiveLevel] > 0
                      ? colorMap[defectiveLevel]
                      : counterColor,
                },
                counterStyle,
              ]}
              trackColor={circleBgColor}
              strokeColor={colorMap[defectiveLevel]}
              labelIcon={
                showIcons ? (
                  <DefectiveIconView
                    defectiveLevel={defectiveLevel}
                    size={iconSize}
                    color={
                      total === 0 && !alwaysEnabled
                        ? disabledIconColor
                        : iconColor
                    }
                    style={iconStyle}
                  />
                ) : undefined
              }
              {...props}
            />
          ) : null,
        )}
      </HStack>
    );
  },
);

const DefectiveIconView = memo(
  ({
    defectiveLevel,
    size,
    color,
    style,
  }: {
    defectiveLevel: DefectiveLevel;
    size?: number;
    color?: string;
    style?: StyleProp<ViewStyle>;
  }) => {
    const Icon = DefectiveIconMap[defectiveLevel];
    return <Icon size={size} color={color} style={style} />;
  },
);
